// forked from prosemirror-dev-tools
import type { MouseEventHandler } from 'react';
import React, { useContext } from 'react';

import { mainTheme as theme } from '../theme.js';

const TabsContextProvider = React.createContext({
  selectedIndex: 'state',
  // eslint-disable-next-line
  onSelect: (_index: string) => {},
});

export const TabList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    data-test-id="__prosemirror_devtools_tabs_buttons_container__"
    style={{
      display: 'flex',
      listStyle: 'none',
      borderBottom: `1px solid ${theme.main20}`,
    }}
  >
    {children}
  </div>
);

const TabsStyled: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    data-test-id="__prosemirror_devtools_tabs_container__"
    style={{
      height: '100%',
      width: '100%',
    }}
  >
    {children}
  </div>
);

type TabStyledProps = {
  isSelected: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
  children: React.ReactNode;
};
const TabStyled: React.FC<TabStyledProps> = ({ children, isSelected, onClick }) => (
  <button
    role="tab"
    type="button"
    style={{
      color: theme.text,
      textTransform: 'uppercase',
      fontSize: '13px',
      padding: '16px 24px 14px',
      boxSizing: 'border-box',
      userSelect: 'none',
      border: 'none',
      borderBottom: isSelected ? `2px solid ${theme.main}` : 'none',
      cursor: 'pointer',
      background: 'transparent',
    }}
    onClick={onClick}
  >
    {children}
  </button>
);

export function Tab({ index, children }: { index: string; children: React.ReactNode }) {
  const tabs = useContext(TabsContextProvider);
  return (
    <TabStyled isSelected={index === tabs.selectedIndex} onClick={() => tabs.onSelect(index)}>
      {children}
    </TabStyled>
  );
}

export function TabPanel(props: { children: (prop: { index: string }) => React.ReactNode }) {
  const tabs = useContext(TabsContextProvider);
  return (
    <div
      style={{
        width: '100%',
        height: 'calc(100% - 48px)',
        boxSizing: 'border-box',
        overflowY: 'auto',
        padding: '8px',
      }}
    >
      {props.children({ index: tabs.selectedIndex })}
    </div>
  );
}

export function Tabs(props: {
  onSelect: (index: string) => void;
  selectedIndex: string;
  children: React.ReactNode;
}) {
  return (
    <TabsContextProvider.Provider
      value={{
        onSelect: props.onSelect,
        selectedIndex: props.selectedIndex,
      }}
    >
      <TabsStyled>{props.children}</TabsStyled>
    </TabsContextProvider.Provider>
  );
}
